import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { type ProfileDetails, purposes } from "../reference-data";
import type { Me } from "../types";
import { PageDialog } from "./ReferenceUI";

export type PreviewProfile = {
  displayName: string;
  age: number | null;
  city: string;
  bio: string | null;
  photos: string[];
  purpose?: string | null;
  occupation?: string | null;
  heightCm?: number | null;
  station?: string | null;
};
function Preview({ profile }: { profile: PreviewProfile }) {
  const [photo, setPhoto] = useState(0);
  const selected = Math.min(photo, Math.max(profile.photos.length - 1, 0));
  const details = [
    ["Я хочу", purposes.find((p) => p.value === profile.purpose)?.label],
    ["Деятельность", profile.occupation],
    ["Рост", profile.heightCm ? `${profile.heightCm} см` : null],
    ["Станция или район", profile.station],
  ];
  return (
    <article className="profile-preview">
      {profile.photos.length > 0 && (
        <div className="profile-preview-gallery">
          <img src={profile.photos[selected]} alt={`Фото ${selected + 1}: ${profile.displayName}`} />
          {profile.photos.length > 1 && (
            <div className="profile-preview-controls">
              <button
                type="button"
                aria-label="Предыдущее фото"
                disabled={selected === 0}
                onClick={() => setPhoto(selected - 1)}
              >
                ‹
              </button>
              <output aria-live="polite">
                {selected + 1} / {profile.photos.length}
              </output>
              <button
                type="button"
                aria-label="Следующее фото"
                disabled={selected === profile.photos.length - 1}
                onClick={() => setPhoto(selected + 1)}
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
      <h2>
        {profile.displayName}
        {profile.age === null ? "" : `, ${profile.age}`}
      </h2>
      <p>{profile.city}</p>
      {profile.bio && <p className="profile-preview-bio">{profile.bio}</p>}
      <dl className="profile-preview-details">
        {details
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
    </article>
  );
}
export function OwnProfilePreview({ me, onClose, onEdit }: { me: Me; onClose(): void; onEdit(): void }) {
  const [details, setDetails] = useState<ProfileDetails | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setDetails(await api<ProfileDetails>("/v1/me/profile-details"));
    } catch {
      setError("Не удалось загрузить дополнительные данные анкеты.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const p = me.profile;
  if (!p) return null;
  const born = new Date(p.birthDate);
  const now = new Date();
  const beforeBirthday =
    now.getUTCMonth() < born.getUTCMonth() ||
    (now.getUTCMonth() === born.getUTCMonth() && now.getUTCDate() < born.getUTCDate());
  const age = now.getUTCFullYear() - born.getUTCFullYear() - Number(beforeBirthday);
  const photos = [...p.photos].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  return (
    <PageDialog title="Моя анкета" onClose={onClose}>
      <Preview
        profile={{
          ...details,
          displayName: p.displayName,
          age: (details?.hideAge ?? p.hideAge) ? null : age,
          city: p.location.name,
          bio: p.bio,
          photos: photos.map((p) => p.url),
        }}
      />
      {p.moderationStatus !== "active" && (
        <p className="reference-note">Анкета на проверке. Фотографии станут видны другим после одобрения.</p>
      )}
      {error && (
        <div className="reference-error" role="alert">
          {error}
          <button type="button" className="secondary-button" onClick={() => void load()}>
            Повторить
          </button>
        </div>
      )}
      <button type="button" className="reference-primary" onClick={onEdit}>
        Изменить
      </button>
    </PageDialog>
  );
}
export function ConversationProfile({ conversationId, onClose }: { conversationId: string; onClose(): void }) {
  const [profile, setProfile] = useState<PreviewProfile | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setProfile((await api<{ profile: PreviewProfile }>(`/v1/conversations/${conversationId}/profile`)).profile);
    } catch {
      setError("Анкета сейчас недоступна. Попробуйте обновить её.");
    }
  }, [conversationId]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <PageDialog title="Анкета собеседника" onClose={onClose}>
      {error ? (
        <div className="reference-error" role="alert">
          {error}
          <button type="button" className="secondary-button" onClick={() => void load()}>
            Повторить
          </button>
        </div>
      ) : profile ? (
        <Preview profile={profile} />
      ) : (
        <p role="status">Загружаем анкету…</p>
      )}
    </PageDialog>
  );
}
